import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { VS_PAGES } from './vsPageData';
import css from './VsPage.module.css';

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <circle cx="9" cy="9" r="9" fill="#2ebff7" fillOpacity="0.15" />
    <path d="M4.5 9l2.8 2.8 5.2-5.5" stroke="#2ebff7" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const XIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M5.5 5.5l7 7M12.5 5.5l-7 7" stroke="#94a3b8" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const VsPageComponent = ({ scrollingDisabled }) => {
  const { competitor } = useParams();
  const history = useHistory();
  const data = VS_PAGES[competitor];

  if (!data) {
    history.replace('/');
    return null;
  }

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faqs.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <Page
      title={`${data.title} — GymNet`}
      description={data.metaDescription}
      scrollingDisabled={scrollingDisabled}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <LayoutSingleColumn>
        <TopbarContainer />

        <div className={css.page}>

          {/* Hero */}
          <section className={css.hero}>
            <div className={css.heroInner}>
              <div className={css.heroPill}>Comparison</div>
              <h1 className={css.heroTitle}>{data.hero.heading}</h1>
              <p className={css.heroSub}>{data.hero.sub}</p>
            </div>
          </section>

          {/* Verdict */}
          <section className={css.verdictSection}>
            <div className={css.verdictInner}>
              <p className={css.verdict}>{data.verdict}</p>
            </div>
          </section>

          {/* Comparison table */}
          <section className={css.tableSection}>
            <div className={css.tableInner}>
              <h2 className={css.tableHeading}>Side-by-Side Comparison</h2>
              <div className={css.tableWrap}>
                <table className={css.table}>
                  <thead>
                    <tr>
                      <th className={css.dimCol} />
                      <th className={css.gymnetCol}>
                        <span className={css.gymnetLabel}>GymNet</span>
                      </th>
                      <th className={css.competitorCol}>
                        <span className={css.competitorLabel}>{data.competitorName}</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.tableRows.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? css.rowEven : css.rowOdd}>
                        <td className={css.dimCell}>{row.dimension}</td>
                        <td className={css.gymnetCell}>
                          {typeof row.gymnet === 'boolean' ? (
                            row.gymnet ? <CheckIcon /> : <XIcon />
                          ) : (
                            <span className={css.gymnetText}>{row.gymnet}</span>
                          )}
                        </td>
                        <td className={css.competitorCell}>
                          {typeof row.competitor === 'boolean' ? (
                            row.competitor ? <CheckIcon /> : <XIcon />
                          ) : (
                            <span className={css.competitorText}>{row.competitor}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          {/* Mid-page CTA */}
          <section className={css.ctaSection}>
            <div className={css.ctaInner}>
              <h2 className={css.ctaTitle}>Find your trainer today</h2>
              <p className={css.ctaSub}>
                Certified, background-checked trainers. No membership. No contract.
              </p>
              <button className={css.ctaBtn} onClick={() => history.push('/s')}>
                Browse Trainers
              </button>
            </div>
          </section>

          {/* FAQ */}
          <section className={css.faqSection}>
            <div className={css.faqInner}>
              <h2 className={css.faqTitle}>Common Questions</h2>
              <div className={css.faqs}>
                {data.faqs.map(({ q, a }) => (
                  <div key={q} className={css.faqItem}>
                    <h3 className={css.faqQ}>{q}</h3>
                    <p className={css.faqA}>{a}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Bottom strip CTA */}
          <section className={css.stripCta}>
            <div className={css.stripCtaInner}>
              <p className={css.stripCtaText}>No membership fees. No contracts. Just great coaching.</p>
              <div className={css.stripCtaBtns}>
                <button className={css.stripCtaMain} onClick={() => history.push('/s')}>
                  Find a Trainer
                </button>
                <button className={css.stripCtaGhost} onClick={() => history.push('/signup')}>
                  Sign up free
                </button>
              </div>
            </div>
          </section>

        </div>

        <FooterContainer />
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

export default compose(connect(mapStateToProps))(VsPageComponent);
